import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  IconAlertCircle,
  IconBrandDiscord,
  IconBrandTelegram,
  IconBrandX,
  IconLoader2,
  IconWallet
} from '@tabler/icons-react';
import { apiClient, endpoints } from '../../../config/apiConfig';
import { creatorService } from '../../../services/api';
import { CREATOR_STATUS_OPTIONS } from '../../../constants/statuses';
import { Button } from '../../ui/button';
import { ConfirmDialog } from '../../ui/confirm-dialog';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Input } from '../../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../ui/select';
import { Textarea } from '../../ui/textarea';
import { useDirtyNavigationGuard } from '../../routing/DirtyNavigationGuard';

const createInitialCreatorData = () => ({
  status: 'BrikctoRecruit',
  discord: { username: '' },
  telegram: { username: '' },
  wallet: { address: '', defaultPayment: '' }
});

export const normalizeTwitterUsername = (value = '') => {
  const trimmedValue = value.trim();
  const profileUrlMatch = trimmedValue.match(
    /^(?:https?:\/\/)?(?:www\.)?(?:x|twitter)\.com\/@?([A-Za-z0-9_]{1,15})(?:[/?#].*)?$/i
  );

  return (profileUrlMatch?.[1] || trimmedValue.replace(/^@/, '')).trim();
};

const getErrorMessage = (error, fallback) => {
  const responseData = error?.response?.data;
  return [responseData?.error, responseData?.message, error?.message]
    .find(message => typeof message === 'string' && message.trim()) || fallback;
};

const CreatorAddWizard = forwardRef(({
  onCreatorAdded,
  onCancel,
  onOpenNotes,
  onSavingChange
}, ref) => {
  const [twitterUsername, setTwitterUsername] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [twitterData, setTwitterData] = useState(null);
  const [existingCreator, setExistingCreator] = useState(null);
  const [notes, setNotes] = useState('');
  const [creatorData, setCreatorData] = useState(createInitialCreatorData);
  const [isSaving, setIsSaving] = useState(false);
  const [walletConflict, setWalletConflict] = useState(null);
  const validatingRef = useRef(false);
  const savingRef = useRef(false);
  const lookupRequestRef = useRef(0);
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;
  const isDirty = Boolean(twitterUsername.trim())
    || Boolean(twitterData)
    || Boolean(existingCreator)
    || Boolean(notes)
    || JSON.stringify(creatorData) !== JSON.stringify(createInitialCreatorData());
  const hasProfileDraft = Boolean(notes)
    || JSON.stringify(creatorData) !== JSON.stringify(createInitialCreatorData());
  const hasCurrentTwitterData = Boolean(
    twitterData
    && normalizeTwitterUsername(twitterUsername).toLowerCase() === twitterData.username.toLowerCase()
  );
  const discardWizard = useCallback(() => onCancelRef.current?.(), []);
  const {
    setDirty,
    setInFlight,
    release,
    confirmAndRelease,
  } = useDirtyNavigationGuard('creator-add-wizard', { onDiscard: discardWizard });

  useEffect(() => {
    setDirty(isDirty);
  }, [isDirty, setDirty]);

  const setSaving = (saving) => {
    savingRef.current = saving;
    setInFlight(saving);
    setIsSaving(saving);
    onSavingChange?.(saving);
  };

  const setValidating = (validating) => {
    validatingRef.current = validating;
    setInFlight(validating);
    setIsValidating(validating);
    onSavingChange?.(validating);
  };

  const requestClose = useCallback(async () => {
    if (validatingRef.current || savingRef.current) return false;
    if (!await confirmAndRelease('Discard this unsaved creator?')) return false;
    onCancelRef.current?.();
    return true;
  }, [confirmAndRelease]);

  useImperativeHandle(ref, () => ({ requestClose }), [requestClose]);

  const handleUsernameChange = (event) => {
    lookupRequestRef.current += 1;
    setTwitterUsername(event.target.value);
    setValidationError('');
    setSubmitError('');
    setExistingCreator(null);
  };

  const validateAndProceed = async (event) => {
    event?.preventDefault();
    if (validatingRef.current || savingRef.current) return;
    const cleanUsername = normalizeTwitterUsername(twitterUsername);

    if (!/^[A-Za-z0-9_]{1,15}$/.test(cleanUsername)) {
      setValidationError('Enter a valid X username, @handle, or profile URL.');
      return;
    }

    const isChangingIdentity = twitterData
      && twitterData.username.toLowerCase() !== cleanUsername.toLowerCase();
    if (isChangingIdentity && hasProfileDraft) {
      const confirmed = await confirmAndRelease('Discard these creator notes and details to find someone else?');
      if (!confirmed) {
        setTwitterUsername(twitterData.username);
        return;
      }
    }

    const requestId = lookupRequestRef.current + 1;
    lookupRequestRef.current = requestId;
    setValidating(true);
    setValidationError('');
    setSubmitError('');

    if (isChangingIdentity) {
      setTwitterData(null);
      setNotes('');
      setCreatorData(createInitialCreatorData());
      setWalletConflict(null);
    }

    try {
      const foundCreator = await creatorService.findCreatorByHandle(cleanUsername);
      if (requestId !== lookupRequestRef.current) return;

      if (foundCreator) {
        setTwitterData(null);
        setExistingCreator(foundCreator);
        setTwitterUsername(foundCreator.twitter?.username || cleanUsername);
        return;
      }

      const twitterResponse = await apiClient.get(endpoints.twitter.user(cleanUsername));
      if (requestId !== lookupRequestRef.current) return;
      const profile = twitterResponse.data?.data || twitterResponse.data;

      if (!profile?.id || !profile?.username || profile.error) {
        setValidationError(getErrorMessage({ response: { data: profile } }, `X user @${cleanUsername} was not found.`));
        return;
      }

      setTwitterData({
        id: profile.id,
        name: profile.name || '',
        username: profile.username,
        followers: Number(profile.followers) || 0,
        profileImageUrl: profile.profile_image_url || '',
        profileUrl: `https://x.com/${profile.username}`
      });
      setExistingCreator(null);
      setTwitterUsername(profile.username);
    } catch (error) {
      if (requestId === lookupRequestRef.current) {
        setValidationError(getErrorMessage(error, 'Could not validate this X username.'));
      }
    } finally {
      setValidating(false);
    }
  };

  const handleNestedInputChange = (parent, child, value) => {
    setCreatorData(previous => ({
      ...previous,
      [parent]: {
        ...previous[parent],
        [child]: value
      }
    }));
    setSubmitError('');
    if (parent === 'wallet' && child === 'address') {
      setWalletConflict(null);
    }
  };

  const createCreator = async (confirmWalletReuse = false) => {
    if (savingRef.current || validatingRef.current || !hasCurrentTwitterData) return;
    if (notes.length > 20000) {
      setSubmitError('Creator notes must be at most 20,000 characters.');
      return;
    }
    setSaving(true);
    setSubmitError('');

    try {
      const payload = {
        twitter: {
          username: twitterData.username,
          id: twitterData.id,
          followers: twitterData.followers,
          profileImageUrl: twitterData.profileImageUrl,
          followersHistory: [{
            count: twitterData.followers,
            date: new Date()
          }]
        },
        notes,
        status: creatorData.status,
        discord: {
          username: creatorData.discord.username.trim(),
          id: ''
        },
        telegram: {
          username: creatorData.telegram.username.trim()
        },
        wallet: {
          address: creatorData.wallet.address.trim(),
          defaultPayment: Number.parseFloat(creatorData.wallet.defaultPayment) || 0
        },
        ...(confirmWalletReuse ? { confirmWalletReuse: true } : {})
      };

      const response = await creatorService.createCreator(payload);
      const createdCreator = response?.data || response;

      if (!createdCreator?._id) {
        throw new Error('The creator was created, but the response was incomplete. Refresh the list to confirm.');
      }

      release();
      onCreatorAdded?.(createdCreator);
    } catch (error) {
      const responseData = error?.response?.data;
      if (responseData?.code === 'WALLET_ALREADY_LINKED') {
        setWalletConflict(responseData.conflictingCreator || {});
      } else if (responseData?.code === 'CREATOR_ALREADY_EXISTS' && responseData.existingCreator) {
        setWalletConflict(null);
        setExistingCreator(responseData.existingCreator);
        setTwitterData(null);
      } else {
        setWalletConflict(null);
        setSubmitError(getErrorMessage(error, 'Failed to add creator.'));
      }
    } finally {
      setSaving(false);
    }
  };

  const finalizeCreator = (event) => {
    event.preventDefault();
    createCreator(false);
  };

  const openExistingNotes = async () => {
    if (!existingCreator || validatingRef.current || savingRef.current) return;
    if (hasProfileDraft) {
      const confirmed = await confirmAndRelease('Discard these unsaved creator notes and open the existing creator?');
      if (!confirmed) return;
    } else {
      release();
    }
    onOpenNotes?.(existingCreator);
  };

  const conflictingCreatorLabel = walletConflict?.username
    ? `@${walletConflict.username}`
    : walletConflict?.name || 'another creator';

  return (
    <>
      <DialogHeader className="pr-8">
        <DialogTitle>Add creator</DialogTitle>
        <DialogDescription>
          Find a creator on X, add private notes, and save them together.
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={hasCurrentTwitterData ? finalizeCreator : validateAndProceed} className="space-y-5">
          <div className="space-y-2">
            <label htmlFor="creator-twitter-username" className="block text-sm font-medium text-text-primary">
              X handle or profile URL
            </label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                id="creator-twitter-username"
                autoFocus
                autoComplete="off"
                value={twitterUsername}
                disabled={isSaving}
                onChange={handleUsernameChange}
                placeholder="@username or x.com/username"
                aria-invalid={Boolean(validationError)}
                aria-describedby={validationError ? 'creator-twitter-error' : 'creator-twitter-help'}
                className="h-11 flex-1"
              />
              <Button
                type="button"
                variant="secondary"
                disabled={!twitterUsername.trim() || isValidating || isSaving}
                onClick={validateAndProceed}
              >
                {isValidating && <IconLoader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
                {isValidating ? 'Finding…' : 'Find creator'}
              </Button>
            </div>
            <p id="creator-twitter-help" className="text-sm text-text-secondary">
              You can paste a handle or full X profile URL.
            </p>
          </div>

          {validationError && (
            <div
              id="creator-twitter-error"
              role="alert"
              className="flex items-start gap-2 border border-error/40 bg-error-muted p-3 text-sm text-error"
            >
              <IconAlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{validationError}</span>
            </div>
          )}

          {existingCreator && (
            <div className="space-y-3 border border-glass-border bg-accent-muted p-4">
              <div>
                <p className="font-medium text-text-primary">Already in your creators</p>
                {existingCreator.name && (
                  <p className="mt-1 text-sm text-text-primary">{existingCreator.name}</p>
                )}
                <p className="text-sm text-text-secondary">
                  @{existingCreator.twitter?.username || normalizeTwitterUsername(twitterUsername)}
                </p>
              </div>
              {notes && <div className="space-y-1"><p className="text-sm text-text-secondary">Your unsaved notes (not added to the existing creator)</p><p className="max-h-40 overflow-auto whitespace-pre-wrap break-words text-sm text-text-primary">{notes}</p></div>}
              <Button type="button" onClick={openExistingNotes}>Open notes</Button>
            </div>
          )}

          {hasCurrentTwitterData && (
            <>
          <div className="flex items-center gap-3 border border-glass-border bg-surface-2 p-3">
            {twitterData.profileImageUrl ? (
              <img
                src={twitterData.profileImageUrl}
                alt=""
                className="h-12 w-12 shrink-0 object-cover"
              />
            ) : (
              <div className="flex h-12 w-12 shrink-0 items-center justify-center bg-surface-3 text-text-secondary">
                <IconBrandX className="h-5 w-5" aria-hidden="true" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              {twitterData.name && (
                <p className="truncate font-medium text-text-primary">{twitterData.name}</p>
              )}
              <a
                href={twitterData.profileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block truncate text-sm text-accent hover:underline"
              >
                @{twitterData.username}
              </a>
              <p className="text-sm text-text-secondary">
                {twitterData.followers.toLocaleString()} followers
              </p>
            </div>
            <a
              href={twitterData.profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open @${twitterData.username} on X`}
              className="p-2 text-text-secondary hover:bg-surface-3 hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <IconBrandX className="h-5 w-5" aria-hidden="true" />
            </a>
          </div>

          <div className="space-y-2">
            <label htmlFor="creator-notes" className="block text-sm font-medium text-text-primary">
              Creator notes
            </label>
            <Textarea
              id="creator-notes"
              rows={7}
              maxLength={20000}
              value={notes}
              disabled={isSaving || isValidating}
              onChange={(event) => {
                setNotes(event.target.value);
                setSubmitError('');
              }}
              placeholder="Add useful context for future campaign decisions…"
              className="min-h-40 resize-y"
            />
            <div className="flex justify-between gap-3 text-xs text-text-secondary">
              <span>Optional: note their content, strengths, audience, and campaign fit.</span>
              <span>{notes.length.toLocaleString()}/20,000</span>
            </div>
          </div>

          <details aria-label="Additional details" className="border border-glass-border bg-surface-2">
            <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
              Additional details
            </summary>
            <div className="space-y-4 border-t border-glass-border p-4">
              <div className="space-y-2">
                <label htmlFor="creator-status" className="block text-sm font-medium text-text-primary">
                  Creator status
                </label>
                <Select
                  disabled={isSaving || isValidating}
                  value={creatorData.status}
                  onValueChange={(value) => {
                    setCreatorData(previous => ({ ...previous, status: value }));
                    setSubmitError('');
                  }}
                >
                  <SelectTrigger id="creator-status" className="h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CREATOR_STATUS_OPTIONS.map(status => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label htmlFor="creator-discord" className="flex items-center gap-1.5 text-sm font-medium text-text-primary">
                <IconBrandDiscord className="h-4 w-4" aria-hidden="true" />
                Discord username
              </label>
              <Input
                id="creator-discord"
                disabled={isSaving || isValidating}
                value={creatorData.discord.username}
                onChange={(event) => handleNestedInputChange('discord', 'username', event.target.value)}
                placeholder="Optional"
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="creator-telegram" className="flex items-center gap-1.5 text-sm font-medium text-text-primary">
                <IconBrandTelegram className="h-4 w-4" aria-hidden="true" />
                Telegram username
              </label>
              <Input
                id="creator-telegram"
                disabled={isSaving || isValidating}
                value={creatorData.telegram.username}
                onChange={(event) => handleNestedInputChange('telegram', 'username', event.target.value)}
                placeholder="Optional"
                className="h-11"
              />
            </div>
          </div>

              <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_9rem]">
            <div className="space-y-2">
              <label htmlFor="creator-wallet" className="flex items-center gap-1.5 text-sm font-medium text-text-primary">
                <IconWallet className="h-4 w-4" aria-hidden="true" />
                Wallet address
              </label>
              <Input
                id="creator-wallet"
                disabled={isSaving || isValidating}
                value={creatorData.wallet.address}
                onChange={(event) => handleNestedInputChange('wallet', 'address', event.target.value)}
                placeholder="Optional"
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="creator-default-payment" className="block text-sm font-medium text-text-primary">
                Default USD
              </label>
              <Input
                id="creator-default-payment"
                disabled={isSaving || isValidating}
                type="number"
                min="0"
                step="0.01"
                value={creatorData.wallet.defaultPayment}
                onChange={(event) => handleNestedInputChange('wallet', 'defaultPayment', event.target.value)}
                placeholder="0.00"
                className="h-11"
              />
            </div>
          </div>
            </div>
          </details>

          {submitError && (
            <div
              role="alert"
              className="flex items-start gap-2 border border-error/40 bg-error-muted p-3 text-sm text-error"
            >
              <IconAlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{submitError}</span>
            </div>
          )}

          <DialogFooter className="gap-2 sm:space-x-0">
            <Button type="button" variant="outline" disabled={isSaving || isValidating} onClick={requestClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving || isValidating}>
              {isSaving && <IconLoader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
              {isSaving ? 'Adding…' : 'Add creator'}
            </Button>
          </DialogFooter>
            </>
          )}

          {!hasCurrentTwitterData && (
            <DialogFooter className="gap-2 sm:space-x-0">
              <Button type="button" variant="outline" disabled={isValidating} onClick={requestClose}>
                Cancel
              </Button>
            </DialogFooter>
          )}
      </form>

      <ConfirmDialog
        open={Boolean(walletConflict)}
        onOpenChange={(open) => {
          if (!open && !isSaving) setWalletConflict(null);
        }}
        onCancel={() => setWalletConflict(null)}
        onConfirm={() => createCreator(true)}
        title="Reuse this wallet?"
        description={`This wallet is already assigned to ${conflictingCreatorLabel}. Continuing means this creator will share a payout destination with one or more existing creators. Only continue if this is intentional.`}
        confirmText="Use wallet anyway"
        cancelText="Keep editing"
        loading={isSaving}
      />
    </>
  );
});

CreatorAddWizard.displayName = 'CreatorAddWizard';

export default CreatorAddWizard;
